// File: src/MathTools/CompositionGraphTool.js

import React from "react";
import { compile } from "mathjs";   // 用 mathjs 處理公式

export default function CompositionGraphTool({
  fEqn = "u^2 + 1",  // mathjs 用 ^ 做平方
  gEqn = "2*x - 1",
  xMin = -5,
  xMax = 5,
  step = 0.1,
  width = 400,
  height = 300,
  margin = 30,
  showAxes = true,
  showTicks = true,
  maxTicks = 20
}) {
  // 用 math.js compile 兩個式子
  let fExpr, gExpr;
  try {
    fExpr = compile(fEqn);
    gExpr = compile(gEqn);
  } catch (err) {
    return <div style={{ color: "red" }}>Invalid formula: {err.message}</div>;
  }

  // 建立 h(x) = f(g(x))
  let points = [];
  for (let x = xMin; x <= xMax; x += step) {
    let gx, hx;
    try {
      gx = gExpr.evaluate({ x });
      hx = fExpr.evaluate({ u: gx });
      // 避免無效值（如 NaN、Infinity）
      if (!isFinite(hx)) continue;
      points.push({ x, y: hx });
    } catch (e) {
      // 跳過不能算的點
      continue;
    }
  }
  if (!points.length) return <div>No composition to plot</div>;

  // y 範圍
  let yMin = Infinity, yMaxi = -Infinity;
  points.forEach(pt => {
    if (pt.y < yMin) yMin = pt.y;
    if (pt.y > yMaxi) yMaxi = pt.y;
  });
  const padY = 0.1 * (yMaxi - yMin || 1); // 防止 yMin==yMax
  yMin -= padY;
  yMaxi += padY;

  // map functions
  const usableWidth = width - 2 * margin;
  const usableHeight = height - 2 * margin;
  function mapX(xVal) {
    const ratio = (xVal - xMin) / (xMax - xMin);
    return margin + ratio * usableWidth;
  }
  function mapY(yVal) {
    const ratio = (yVal - yMin) / (yMaxi - yMin);
    return margin + (1 - ratio) * usableHeight;
  }

  // build path
  const pathD = points.map((pt, i) => {
    const sx = mapX(pt.x);
    const sy = mapY(pt.y);
    return i === 0 ? `M ${sx},${sy}` : `L ${sx},${sy}`;
  }).join(" ");

  // minimal axes, ticks
  let xAxisLine = null, yAxisLine = null;
  if (showAxes) {
    if (yMin < 0 && yMaxi > 0) {
      xAxisLine = <line
        x1={mapX(xMin)} y1={mapY(0)}
        x2={mapX(xMax)} y2={mapY(0)}
        stroke="gray"
        strokeWidth={1}
      />;
    }
    if (xMin < 0 && xMax > 0) {
      yAxisLine = <line
        x1={mapX(0)} y1={mapY(yMin)}
        x2={mapX(0)} y2={mapY(yMaxi)}
        stroke="gray"
        strokeWidth={1}
      />;
    }
  }

  return (
    <div style={{ textAlign: "center" }}>
      <svg width={width} height={height} style={{ border: "1px solid #ccc" }}>
        {xAxisLine}
        {yAxisLine}
        <path d={pathD} fill="none" stroke="blue" strokeWidth={2} />
      </svg>
      <div style={{ fontSize: "0.9em", marginTop: 4 }}>
        <em>CompositionGraphTool: h(x) = f(g(x)) with f(u) = {fEqn}, g(x) = {gEqn}.</em>
      </div>
    </div>
  );
}
